/**
 * Epic 9 — generování change scriptu. Stejný celkový vzor jako `SyncPanel.tsx`
 * (Epic 2): sebeobsažené tlačítko + modál v top-right panelu canvasu, re-scan
 * DB přes STEJNÉ `api.startSyncScan`/`getSyncScanJob` job jako Epic 2 (žádná
 * paralelní scan infrastruktura).
 *
 * Pre-flight (E9-US1 až US3) počítá dvě věci čistě na klientovi
 * (`changeScript.ts#buildDriftCheck`, sdílené se `sync.ts`/`diff.ts`):
 * DB drift (baseline vs. čerstvý re-scan) a konflikty s tím, co uživatel
 * mezitím upravil v modelu (baseline vs. model, rename-aware). Nekonfliktní
 * drift + konflikty vyřešené jako "Přijmout stav z DB" se slučují do modelu
 * PŘESNĚ stejnou cestou jako Epic 2 (`canvas/syncApply.ts#buildApplyBundle`,
 * vytažené ze `SyncPanel.tsx` právě proto, aby ho tahle epika mohla sdílet).
 *
 * Klíčové rozhodnutí (`doc/stav-implementace.md`, plán tohoto průchodu):
 * baseline se po pre-flightu nastaví na CELÝ čerstvě naskenovaný stav DB,
 * bez ohledu na to, co bylo vybráno k mergi do modelu — `applySyncBaseline`
 * dostane `rescannedSchema` jako nepovinný `baselineSchema` parametr (Epic 9
 * rozšíření, beze změny chování pro Epic 2's vlastní volání). Díky tomu
 * konflikt vyřešený jako "Zachovat mou změnu" (model se NEZMĚNÍ, jen se
 * nezaškrtne v mergi) automaticky vyjde ve finálním diffu (baseline vs. model)
 * jako čistá položka počítaná proti AKTUÁLNÍ DB, ne proti staré baseline.
 *
 * Samotné generování skriptu (poslední krok) žádnou DB neskenuje ani baseline
 * neposouvá (epic-9 kap. 2) — jen zavolá `POST /change-script/generate` s
 * vybranými klíči finálního diffu.
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Copy, Database, Download, FileCode2, RefreshCw, User, Wand2, X } from 'lucide-react';
import type { CanvasMetadata, ConflictItem, ConflictResolution, SchemaModel, SyncDiffItem, SyncScanJobStatus } from '@bits2schema/shared';
import { buildChangeScriptDiff, buildDriftCheck, diffItemKey } from '@bits2schema/shared';
import { api } from '../api/client';
import { useCommandStack } from '../command/stack';
import { Modal } from '../components/Modal';
import { SqlPreview } from '../components/SqlPreview';
import { useT } from '../i18n';
import type { MessageKey } from '../i18n/types';
import { DIFF_CHANGE_TYPE_KEYS, DIFF_OBJECT_TYPE_KEYS } from '../utils/diffLabels';
import { describeError, formatDateTime } from '../utils/format';
import type { Command as CanvasCommand } from './commands';
import type { BoxSize } from './geometry';
import type { Point } from './layout';
import { buildApplyBundle } from './syncApply';
import type { SchemaCommand } from '../schema/commands';
import { useI18n } from '../i18n';

const SYNC_PHASE_KEYS: Record<SyncScanJobStatus['phase'], MessageKey> = {
  connecting: 'import.phase.connecting',
  readingTables: 'import.phase.readingTables',
  readingColumns: 'import.phase.readingColumns',
  readingConstraints: 'import.phase.readingConstraints',
  readingIndexes: 'import.phase.readingIndexes',
  buildingModel: 'import.phase.buildingModel',
  savingProject: 'import.phase.savingProject',
  computingDiff: 'sync.phase.computingDiff',
  done: 'import.phase.done',
};

const POLL_INTERVAL_MS = 500;

type Phase =
  | { kind: 'idle' }
  | { kind: 'loadingContext' }
  | { kind: 'scanning'; job: SyncScanJobStatus; lastSyncAt: string | null }
  | {
      kind: 'resolvingConflicts';
      lastSyncAt: string | null;
      baselineSchema: SchemaModel;
      rescannedSchema: SchemaModel;
      driftItems: SyncDiffItem[];
      conflicts: ConflictItem[];
    }
  | {
      kind: 'reviewingDrift';
      lastSyncAt: string | null;
      baselineSchema: SchemaModel;
      rescannedSchema: SchemaModel;
      driftItems: SyncDiffItem[];
      lockedKeys: Set<string>;
    }
  | { kind: 'applyingDrift' }
  | { kind: 'reviewingFinalDiff'; baselineSchema: SchemaModel; model: SchemaModel; diff: SyncDiffItem[] }
  | { kind: 'generating' }
  | { kind: 'result'; script: string; statementCount: number }
  | { kind: 'error'; message: string };

interface ChangeScriptPanelProps {
  projectId: string;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  schemaExecute: (command: SchemaCommand) => void;
  canvasExecute: (command: CanvasCommand) => void;
  effectiveTablePositions: Record<string, Point>;
  tableSizes: Record<string, BoxSize>;
  /** E9-US7 — "Skript jsem spustil → synchronizovat" otevře standardní Epic 2 sync flow (`SyncPanel`). */
  onRequestSync: () => void;
}

export function ChangeScriptPanel({
  projectId,
  schema,
  canvas,
  schemaExecute,
  canvasExecute,
  effectiveTablePositions,
  tableSizes,
  onRequestSync,
}: ChangeScriptPanelProps) {
  const t = useT();
  const { language } = useI18n();
  const { clear } = useCommandStack();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

  const closePanel = useCallback(() => {
    setOpen(false);
    setPhase({ kind: 'idle' });
  }, []);

  const start = useCallback(async () => {
    setOpen(true);
    setPhase({ kind: 'loadingContext' });
    try {
      const baseline = await api.getSyncBaseline(projectId);
      if (!baseline.schema || !baseline.entry) {
        setPhase({ kind: 'error', message: t('changeScript.noBaseline') });
        return;
      }
      const lastSyncAt = baseline.entry.timestamp;
      setPhase({
        kind: 'scanning',
        lastSyncAt,
        job: {
          jobId: '',
          state: 'running',
          phase: 'connecting',
          tablesDone: 0,
          tablesTotal: null,
          startedAt: new Date().toISOString(),
          finishedAt: null,
        },
      });
      const { jobId } = await api.startSyncScan(projectId);
      setPhase((prev) => (prev.kind === 'scanning' ? { ...prev, job: { ...prev.job, jobId } } : prev));
      pollScan(jobId, baseline.schema, lastSyncAt);
    } catch (caught) {
      setPhase({ kind: 'error', message: describeError(caught, t) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, t]);

  const pollScan = useCallback(
    (jobId: string, baselineSchema: SchemaModel, lastSyncAt: string | null) => {
      const timer = window.setInterval(async () => {
        try {
          const job = await api.getSyncScanJob(projectId, jobId);
          if (job.state === 'running') {
            setPhase((prev) => (prev.kind === 'scanning' ? { ...prev, job } : prev));
            return;
          }
          window.clearInterval(timer);
          if (job.state !== 'succeeded' || !job.rescannedSchema) {
            setPhase({ kind: 'error', message: job.error?.message ?? t('common.unknownError') });
            return;
          }
          const rescannedSchema = job.rescannedSchema;
          const driftCheck = buildDriftCheck(baselineSchema, rescannedSchema, schema);
          if (!driftCheck.hasDrift) {
            setPhase({
              kind: 'reviewingFinalDiff',
              baselineSchema,
              model: schema,
              diff: buildChangeScriptDiff(baselineSchema, schema),
            });
            return;
          }
          if (driftCheck.conflicts.length > 0) {
            setPhase({
              kind: 'resolvingConflicts',
              lastSyncAt,
              baselineSchema,
              rescannedSchema,
              driftItems: driftCheck.driftItems,
              conflicts: driftCheck.conflicts,
            });
          } else {
            setPhase({
              kind: 'reviewingDrift',
              lastSyncAt,
              baselineSchema,
              rescannedSchema,
              driftItems: driftCheck.driftItems,
              lockedKeys: new Set(),
            });
          }
        } catch (caught) {
          window.clearInterval(timer);
          setPhase({ kind: 'error', message: describeError(caught, t) });
        }
      }, POLL_INTERVAL_MS);
    },
    [projectId, schema, t],
  );

  const resolveConflict = useCallback((key: string, resolution: ConflictResolution) => {
    setPhase((prev) => {
      if (prev.kind !== 'resolvingConflicts') return prev;
      return {
        ...prev,
        conflicts: prev.conflicts.map((conflict) => (conflict.key === key ? { ...conflict, resolution } : conflict)),
      };
    });
  }, []);

  const continueFromConflicts = useCallback(() => {
    setPhase((prev) => {
      if (prev.kind !== 'resolvingConflicts') return prev;
      const resolutionByKey = new Map(prev.conflicts.map((conflict) => [conflict.key, conflict.resolution]));
      const driftItems = prev.driftItems.map((item) => {
        const resolution = resolutionByKey.get(diffItemKey(item));
        if (!resolution) return item;
        return { ...item, selected: resolution === 'acceptDb' };
      });
      return {
        kind: 'reviewingDrift',
        lastSyncAt: prev.lastSyncAt,
        baselineSchema: prev.baselineSchema,
        rescannedSchema: prev.rescannedSchema,
        driftItems,
        lockedKeys: new Set(resolutionByKey.keys()),
      };
    });
  }, []);

  const toggleDriftItem = useCallback((key: string) => {
    setPhase((prev) => {
      if (prev.kind !== 'reviewingDrift' || prev.lockedKeys.has(key)) return prev;
      return {
        ...prev,
        driftItems: prev.driftItems.map((item) => (diffItemKey(item) === key ? { ...item, selected: !item.selected } : item)),
      };
    });
  }, []);

  const applyDriftAndContinue = useCallback(async () => {
    if (phase.kind !== 'reviewingDrift') return;
    const { baselineSchema, rescannedSchema, driftItems } = phase;
    setPhase({ kind: 'applyingDrift' });
    try {
      const bundle = buildApplyBundle(schema, canvas, rescannedSchema, driftItems, effectiveTablePositions, tableSizes);
      for (const command of bundle.plan.commands) schemaExecute(command);
      for (const command of bundle.canvasCommands) canvasExecute(command);
      await api.applySyncBaseline(projectId, bundle.plan.mergedSchema, bundle.mergedCanvas, t('changeScript.preflight.baselineLabel'), rescannedSchema);
      // Stejná nevratná operace jako Epic 2's merge (`doc/epic-13-undo-redo.md` kap. 2).
      clear();
      setPhase({
        kind: 'reviewingFinalDiff',
        baselineSchema: rescannedSchema,
        model: bundle.plan.mergedSchema,
        diff: buildChangeScriptDiff(rescannedSchema, bundle.plan.mergedSchema),
      });
    } catch (caught) {
      setPhase({ kind: 'error', message: describeError(caught, t) });
    }
  }, [phase, schema, canvas, effectiveTablePositions, tableSizes, schemaExecute, canvasExecute, projectId, t, clear]);

  const toggleFinalItem = useCallback((key: string) => {
    setPhase((prev) => {
      if (prev.kind !== 'reviewingFinalDiff') return prev;
      return { ...prev, diff: prev.diff.map((item) => (diffItemKey(item) === key ? { ...item, selected: !item.selected } : item)) };
    });
  }, []);

  const generate = useCallback(async () => {
    if (phase.kind !== 'reviewingFinalDiff') return;
    const selectedKeys = phase.diff.filter((item) => item.selected).map((item) => diffItemKey(item));
    setPhase({ kind: 'generating' });
    try {
      const result = await api.generateChangeScript(projectId, selectedKeys);
      setPhase({ kind: 'result', script: result.script, statementCount: result.statementCount });
    } catch (caught) {
      setPhase({ kind: 'error', message: describeError(caught, t) });
    }
  }, [phase, projectId, t]);

  const runSyncShortcut = useCallback(() => {
    closePanel();
    onRequestSync();
  }, [closePanel, onRequestSync]);

  const downloadScript = useCallback((script: string) => {
    const blob = new Blob([script], { type: 'application/sql' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'change-script.sql';
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  const copyScript = useCallback(async (script: string) => {
    await navigator.clipboard.writeText(script);
  }, []);

  return (
    <>
      <button type="button" onClick={() => void start()}>
        <FileCode2 size={16} />
        {t('changeScript.button')}
      </button>

      {open && (
        <Modal
          title={t('changeScript.title')}
          onClose={phase.kind === 'applyingDrift' || phase.kind === 'generating' ? () => {} : closePanel}
          wide
          actions={renderActions()}
        >
          <div className="stack sync-panel">
            {'lastSyncAt' in phase && (
              <p className="hint">
                {phase.lastSyncAt ? t('changeScript.lastSync', { time: formatDateTime(phase.lastSyncAt, t, language) }) : t('changeScript.lastSyncNever')}
              </p>
            )}

            {phase.kind === 'loadingContext' && <p className="hint">{t('common.loading')}</p>}

            {phase.kind === 'scanning' && (
              <div className="stack">
                <h3>{t(SYNC_PHASE_KEYS[phase.job.phase])}</h3>
                <div className="progress">
                  <div
                    className="progress__bar"
                    style={{
                      width: `${
                        phase.job.tablesTotal && phase.job.tablesTotal > 0
                          ? Math.min(100, Math.round((phase.job.tablesDone / phase.job.tablesTotal) * 100))
                          : 15
                      }%`,
                    }}
                  />
                </div>
              </div>
            )}

            {phase.kind === 'resolvingConflicts' && (
              <div className="stack">
                <div className="notice notice--warning">{t('changeScript.conflicts.intro', { count: phase.conflicts.length })}</div>
                {phase.conflicts.map((conflict) => (
                  <div key={conflict.key} className="change-script-conflict">
                    <strong>
                      {t(DIFF_OBJECT_TYPE_KEYS[conflict.objectType])}: {conflict.path}
                    </strong>
                    <div className="change-script-conflict__values">
                      <div className="change-script-conflict__value">
                        <strong>{t('changeScript.conflicts.baseline')}</strong>
                        {conflict.baselineValue ?? '—'}
                      </div>
                      <div className="change-script-conflict__value">
                        <strong>{t('changeScript.conflicts.db')}</strong>
                        {conflict.dbValue ?? '—'}
                      </div>
                      <div className="change-script-conflict__value">
                        <strong>{t('changeScript.conflicts.model')}</strong>
                        {conflict.modelValue ?? '—'}
                      </div>
                    </div>
                    <div className="change-script-conflict__actions">
                      <button
                        type="button"
                        aria-pressed={conflict.resolution === 'acceptDb'}
                        onClick={() => resolveConflict(conflict.key, 'acceptDb')}
                      >
                        <Database size={16} />
                        {t('changeScript.conflicts.acceptDb')}
                      </button>
                      <button
                        type="button"
                        aria-pressed={conflict.resolution === 'keepMine'}
                        onClick={() => resolveConflict(conflict.key, 'keepMine')}
                      >
                        <User size={16} />
                        {t('changeScript.conflicts.keepMine')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {phase.kind === 'reviewingDrift' && renderDiffTable(phase.driftItems, toggleDriftItem, phase.lockedKeys)}
            {phase.kind === 'applyingDrift' && <p className="hint">{t('sync.applying')}</p>}

            {phase.kind === 'reviewingFinalDiff' &&
              (phase.diff.length === 0 ? (
                <div className="notice">{t('changeScript.finalDiff.empty')}</div>
              ) : (
                renderDiffTable(phase.diff, toggleFinalItem, new Set())
              ))}

            {phase.kind === 'generating' && <p className="hint">{t('changeScript.generating')}</p>}

            {phase.kind === 'result' && (
              <div className="stack">
                <div className="notice notice--success">{t('changeScript.result.summary', { count: phase.statementCount })}</div>
                <SqlPreview sql={phase.script} />
              </div>
            )}

            {phase.kind === 'error' && <div className="notice notice--error">{phase.message}</div>}
          </div>
        </Modal>
      )}
    </>
  );

  function renderDiffTable(items: SyncDiffItem[], onToggle: (key: string) => void, lockedKeys: Set<string>) {
    if (items.length === 0) return <div className="notice">{t('sync.noDifferences')}</div>;
    return (
      <table>
        <thead>
          <tr>
            <th />
            <th>{t('history.diff.column.object')}</th>
            <th>{t('history.diff.column.path')}</th>
            <th>{t('history.diff.column.old')}</th>
            <th>{t('history.diff.column.new')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = diffItemKey(item);
            const locked = lockedKeys.has(key);
            return (
              <tr key={key} className={`diff-row diff-row--${item.changeType}`}>
                <td>
                  <input type="checkbox" checked={item.selected} disabled={locked} onChange={() => onToggle(key)} />
                </td>
                <td>
                  <span className="badge">{t(DIFF_OBJECT_TYPE_KEYS[item.objectType])}</span>
                  <div className="hint">{t(DIFF_CHANGE_TYPE_KEYS[item.changeType])}</div>
                </td>
                <td>{item.path}</td>
                <td>{item.oldValue ?? '—'}</td>
                <td>{item.newValue ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderActions() {
    switch (phase.kind) {
      case 'resolvingConflicts':
        return (
          <>
            <button type="button" onClick={closePanel}>
              <X size={16} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="primary"
              disabled={phase.conflicts.some((conflict) => conflict.resolution === 'unresolved')}
              onClick={continueFromConflicts}
            >
              {t('changeScript.conflicts.continue')}
              <ArrowRight size={16} />
            </button>
          </>
        );
      case 'reviewingDrift':
        return (
          <>
            <button type="button" onClick={closePanel}>
              <X size={16} />
              {t('common.cancel')}
            </button>
            <button type="button" className="primary" onClick={() => void applyDriftAndContinue()}>
              {t('changeScript.preflight.continue')}
              <ArrowRight size={16} />
            </button>
          </>
        );
      case 'reviewingFinalDiff':
        return (
          <>
            <button type="button" onClick={closePanel}>
              <X size={16} />
              {t('common.cancel')}
            </button>
            <button type="button" className="primary" disabled={phase.diff.every((item) => !item.selected)} onClick={() => void generate()}>
              <Wand2 size={16} />
              {t('changeScript.generateButton')}
            </button>
          </>
        );
      case 'result':
        return (
          <>
            <button type="button" onClick={() => void copyScript(phase.script)}>
              <Copy size={16} />
              {t('changeScript.result.copy')}
            </button>
            <button type="button" onClick={() => downloadScript(phase.script)}>
              <Download size={16} />
              {t('changeScript.result.download')}
            </button>
            <button type="button" className="primary" onClick={runSyncShortcut}>
              <RefreshCw size={16} />
              {t('changeScript.result.ranIt')}
            </button>
            <button type="button" onClick={closePanel}>
              <X size={16} />
              {t('common.close')}
            </button>
          </>
        );
      case 'error':
        return (
          <button type="button" className="primary" onClick={closePanel}>
            <X size={16} />
            {t('common.close')}
          </button>
        );
      default:
        return (
          <button type="button" disabled={phase.kind === 'applyingDrift' || phase.kind === 'generating'} onClick={closePanel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
        );
    }
  }
}
